import { FilesApi, FileShape } from '@cibo/core'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFiles } from '../../../../queries/useFiles'
import { downloadAxiosResponse, intuitiveFromNow } from '../../../../utils'
import { ResponsiveDialog } from '../../../ResponsiveDialog'
import { formatBytes } from './utils'

type AddExistingFileModalProps = {
  open: boolean
  onClose(): void
  onAdd(file: FileShape): void
  extensions: string[]
  maxSizeBytes?: number
  alreadySelectedIds: string[]
}

export function AddExistingFileModal({
  open,
  onClose,
  onAdd,
  extensions,
  maxSizeBytes,
  alreadySelectedIds,
}: AddExistingFileModalProps) {
  const { t } = useTranslation('@cibo/ui/FileUploadField')
  const files = useFiles()

  const handleDownload = useCallback((file: FileShape) => {
    FilesApi.downloadFile(file.id).then(downloadAxiosResponse(file.name))
  }, [])

  const filtered = files.data
    ?.filter(file => !alreadySelectedIds.includes(file.id))
    .filter(file => !maxSizeBytes || file.bytes <= maxSizeBytes)
    .filter(file => {
      const ext =
        file.name
          .split('.')
          .pop()
          ?.toLowerCase() ?? ''
      return extensions.includes(ext)
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('addExistingFile')}</DialogTitle>
      <DialogContent>
        {!filtered?.length ? (
          <Typography variant="body2" color="textSecondary" fontStyle="italic" align="center">
            {t('noExistingFiles')}
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {filtered.map(file => (
                <TableRow key={file.id}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2">{file.name}</Typography>
                      <Tooltip title={t('downloadFile')}>
                        <IconButton size="small" onClick={() => handleDownload(file)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatBytes(file.bytes)}</TableCell>
                  <TableCell>{intuitiveFromNow(file.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      data-testid={`add-existing-file-${file.id}`}
                      variant="contained"
                      size="small"
                      onClick={() => {
                        onAdd(file)
                        onClose()
                      }}
                    >
                      {t('addFile')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} data-testid="add-existing-file-cancel-button">
          {t('close')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
